import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Form,
  Select,
  Button,
  Alert,
  Typography,
  Card,
  Row,
  Col,
  Input,
  InputNumber,
  Radio,
  Table,
  Tag,
} from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import * as api from 'util/api';
import STORES from 'config/stores';
import { getCountryFromIP } from 'util/geolocation';
import {
  inferDtcPackFromSku,
  kegLitresLabelFromUnitMl,
  resolveShopifyUnitBarcode,
} from 'util/dtc-shopify-barcode';
import {
  generateShopifyProductPayload,
  resolveDtcPackConfigKey,
} from './utils/generateShopifyProductPayload';
import { resolveDtcVariantPricing } from './utils/dtcVariantPricing';

const { Text } = Typography;
const { Option } = Select;

const filterOptions = ['Limited', 'Core', 'Return', 'Vintage', 'Collab', 'FRESH',
  'Pack', 'Wild Workshop', 'IPA', 'West Coast', 'Hazy', 'Lager', 'Dark', 'Ale', 'Pale Ale', 'Sour', 'Low & No',
];

/** Same shape as legacy DTCProductBuilder / DTCProductModal (axios error from /shopify/products/create). */
function getShopifyCreateErrorMessage(error) {
  const userError = error?.response?.data?.error?.userError;
  if (userError) return userError;
  if (error?.message && typeof error.message === 'string') return error.message;
  return 'There was an error generating the product';
}

function getRRPOptions(rrp) {
  if (!rrp) {
    return [];
  }
  if (/^\d+(\.\d+)?$/.test(rrp)) return [rrp];
  return rrp.split(/[ ,/]+/).map((e) => e.replace(/[^\d.]/g, '')).filter(Boolean);
}

function formatUnitAmount(value) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return String(value ?? '');
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function sourceLabel(row, unitPrice) {
  const unit = formatUnitAmount(unitPrice);
  if (row.source === 'unit') return 'Selected RRP';
  if (row.source === 'sku') {
    const rrp = row.sku?.rrpPack ?? row.price;
    const packName = String(row.title || '').replace('-', ' ');
    return `${packName} SKU is $${rrp}`;
  }
  const label = row.formulaLabel || '';
  const keepMatch = label.match(/×\s*n\s*×\s*([\d.]+)/);
  if (keepMatch) {
    const keep = parseFloat(keepMatch[1]);
    if (!Number.isNaN(keep) && keep > 0 && keep < 1) {
      const pct = Math.round((1 - keep) * 1000) / 10;
      return `$${unit} × ${row.count} with ${pct}% discount`;
    }
  }
  if (label.includes('12')) {
    return `$${unit} × 12 − $12`;
  }
  return `$${unit} × ${row.count}`;
}

function mergeRrpOptions(baseOptions, extra) {
  if (extra == null || extra === '') return baseOptions;
  const s = String(extra).trim();
  if (!s || baseOptions.includes(s)) return baseOptions;
  return [s, ...baseOptions];
}

function StoreSelect({ value, onChange, defaultStore }) {
  return (
    <Select
      className="generator-store-button"
      defaultValue={defaultStore}
      style={{ width: 180 }}
      value={value}
      onChange={onChange}
    >
      <Option value={STORES.GPNZ}>Garage Project NZ</Option>
      <Option value={STORES.GPAU}>Garage Project AU</Option>
    </Select>
  );
}

StoreSelect.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  defaultStore: PropTypes.string,
};

StoreSelect.defaultProps = {
  defaultStore: undefined,
};

const NewDTCProductBuilder = ({
  prices,
  rules,
}) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState(null);
  const [productId, setProductId] = useState(null);
  const [skus, setSkus] = useState([]);
  const [skuId, setSkuId] = useState(null);
  const [loadingSkus, setLoadingSkus] = useState(false);
  const [store, setStore] = useState(STORES.GPNZ);
  const [defaultStore, setDefaultStore] = useState();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  const lastResetKeyRef = useRef(null);
  const watchedRrp = Form.useWatch('selectedRRP', form);
  const watchedSlab = Form.useWatch('selectedSlab', form);

  useEffect(() => {
    (async () => {
      const country = await getCountryFromIP();
      setDefaultStore(country.code === 'AU' ? STORES.GPAU : STORES.GPNZ);
    })();
  }, []);

  useEffect(() => {
    api.getProducts().then(setProducts);
  }, []);

  useEffect(() => {
    setSubmitError(null);
    setSubmitSuccess(null);
  }, [productId, skuId, store]);

  useEffect(() => {
    if (!productId) {
      setSkus([]);
      setSkuId(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingSkus(true);
    api.getProductSkus(productId)
      .then((data) => {
        if (!cancelled) {
          setSkus(Array.isArray(data) ? data : []);
          setSkuId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSkus(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const product = useMemo(
    () => (products && productId ? products.find((p) => p.id === productId) : null),
    [products, productId],
  );

  const productWithSkus = useMemo(
    () => (product ? { ...product, skus } : null),
    [product, skus],
  );

  const selectedSku = useMemo(() => {
    if (!skuId || !skus.length) return null;
    return skus.find((s) => String(s.key ?? s.id) === String(skuId)) ?? null;
  }, [skuId, skus]);

  const selectableSkus = useMemo(
    () => skus.filter((s) => !s.deleted && !s.isLegacy),
    [skus],
  );

  const dtcPack = useMemo(
    () => (selectedSku ? inferDtcPackFromSku(selectedSku) : null),
    [selectedSku],
  );

  const rrpOptions = useMemo(() => {
    if (!product) return [];
    const legacyRrp = store === 'GPAU' ? product.rrpAud : product.rrp;
    let opts = getRRPOptions(legacyRrp);
    const fromSku = selectedSku?.rrpUnit;
    opts = mergeRrpOptions(opts, fromSku);
    return opts;
  }, [product, store, selectedSku]);

  useEffect(() => {
    const key = `${productId}|${skuId}|${store}`;
    if (!product || !selectedSku || !dtcPack) {
      lastResetKeyRef.current = null;
      return;
    }
    if (lastResetKeyRef.current === key) return;
    lastResetKeyRef.current = key;

    const legacyRrp = store === 'GPAU' ? product.rrpAud : product.rrp;
    const preferred = (selectedSku.rrpUnit && String(selectedSku.rrpUnit).trim())
      || (legacyRrp && getRRPOptions(legacyRrp)[0])
      || undefined;
    const caseCount = Number.isFinite(dtcPack.caseCount) ? dtcPack.caseCount : null;
    let slabDefault = 15;
    if (dtcPack.packType === 'Can' && [12, 15, 16, 24].includes(caseCount)) {
      slabDefault = caseCount;
    } else if (dtcPack.unitSize === '330'
      || (dtcPack.unitSize === '375' && dtcPack.packType === 'Can')) {
      slabDefault = 24;
    }

    form.setFieldsValue({
      unitSize: dtcPack.unitSize,
      kegMl: dtcPack.packType === 'Keg' ? dtcPack.unitMl ?? undefined : undefined,
      selectedRRP: preferred,
      selectedSlab: slabDefault,
      productFilters: [],
      SKU: undefined,
    });
  }, [product, selectedSku, dtcPack, productId, skuId, store, form]);

  const productOptions = useMemo(() => (
    (products ?? []).map((p) => ({ label: p.name, value: p.id }))
  ), [products]);

  const skuOptions = useMemo(() => (
    selectableSkus.map((s) => ({
      label: s.name || `SKU ${s.key ?? s.id}`,
      value: s.key ?? s.id,
    }))
  ), [selectableSkus]);

  const variantPricing = useMemo(() => {
    if (!dtcPack || watchedRrp == null || watchedRrp === '') return null;
    const defaultSlab = dtcPack.unitSize === '330'
      || (dtcPack.unitSize === '375' && dtcPack.packType === 'Can')
      ? 24
      : 15;
    return resolveDtcVariantPricing({
      store,
      unitSize: dtcPack.unitSize,
      packConfigKey: resolveDtcPackConfigKey(dtcPack.unitSize, dtcPack.packType),
      packType: dtcPack.packType,
      unitPrice: watchedRrp,
      slabCount: watchedSlab ?? dtcPack.caseCount ?? defaultSlab,
      skus,
      dtcPrices: prices,
      rules,
    });
  }, [dtcPack, watchedRrp, watchedSlab, store, skus, prices, rules]);

  const handleSubmit = async (formData) => {
    if (!productWithSkus || !dtcPack) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      const unitBarcode = resolveShopifyUnitBarcode(
        productWithSkus,
        dtcPack.unitSize,
        dtcPack.packType,
        selectedSku,
      );
      const payload = generateShopifyProductPayload({
        store,
        product: productWithSkus,
        formatPackType: dtcPack.packType,
        formData: {
          ...formData,
          unitSize: dtcPack.unitSize,
          kegMl: dtcPack.packType === 'Keg' ? formData.kegMl ?? dtcPack.unitMl : undefined,
          selectedSlab: formData.selectedSlab ?? dtcPack.caseCount ?? undefined,
          selectedRRP: formData.selectedRRP != null && formData.selectedRRP !== ''
            ? String(formData.selectedRRP)
            : formData.selectedRRP,
        },
        dtcPrices: prices,
        rules,
        unitBarcodeOverride: unitBarcode,
      });
      const result = await api.createNewShopifyProduct(store, payload, {
        productMatrixId: productWithSkus.id,
      });
      const linked = result?.links?.linked ?? 0;
      const failed = result?.links?.failed ?? 0;
      if (failed > 0) {
        setSubmitSuccess(
          `Created. Linked ${linked} SKU(s); ${failed} could not be linked.`,
        );
      } else if (linked > 0) {
        setSubmitSuccess(`Created and linked to ${linked} SKU(s).`);
      } else {
        setSubmitSuccess('Product created.');
      }
    } catch (e) {
      setSubmitError(getShopifyCreateErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const selectCommon = {
    size: 'large',
    showSearch: true,
    allowClear: true,
    optionFilterProp: 'label',
    style: { width: '100%' },
  };

  const unsupportedSku = selectedSku && !dtcPack;
  const showForm = product && selectedSku && dtcPack;

  return (
    <div className="new-dtc-product-builder" style={{ maxWidth: 640, margin: '0 auto' }}>
      <Card title="Store" style={{ marginBottom: 16 }}>
        <StoreSelect
          value={store}
          onChange={setStore}
          defaultStore={defaultStore}
        />
      </Card>

      <Card title="Product & SKU" loading={products === null}>
        <Form layout="vertical" requiredMark={false}>
          <Row gutter={[16, 0]}>
            <Col xs={24} lg={12}>
              <Form.Item label="Product" htmlFor="new-dtc-product">
                <Select
                  {...selectCommon}
                  id="new-dtc-product"
                  placeholder="Search and select a product"
                  options={productOptions}
                  filterOption={(input, option) => (
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  )}
                  value={productId}
                  onChange={(v) => {
                    setProductId(v ?? null);
                    setSkuId(null);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="SKU" htmlFor="new-dtc-sku">
                <Select
                  {...selectCommon}
                  id="new-dtc-sku"
                  loading={loadingSkus}
                  disabled={!productId || loadingSkus}
                  placeholder={productId ? 'Select single-unit SKU (can, bottle, or keg)' : 'Select a product first'}
                  options={skuOptions}
                  filterOption={(input, option) => (
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  )}
                  value={skuId}
                  onChange={setSkuId}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {unsupportedSku && (
        <Alert
          style={{ marginTop: 16 }}
          type="warning"
          showIcon
          message="This SKU is not supported in the SKU-first builder"
          description={(
            <>
              Only single-unit 330ml / 375ml / 440ml cans, 375ml / 750ml bottles, or 19.5–50L kegs are supported here.
              Use the legacy builder for other formats (e.g. 250ml, multi-pack SKUs):
              {' '}
              <Link to="/dashboard/dtc-product-builder/shopify">Legacy DTC Product Builder</Link>
              .
            </>
          )}
        />
      )}

      {showForm && (
        <Card title="Shopify product details" style={{ marginTop: 16 }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            {submitError && (
              <Alert
                type="error"
                showIcon
                closable
                message={submitError}
                onClose={() => setSubmitError(null)}
                style={{ marginBottom: 16 }}
              />
            )}
            {submitSuccess && (
              <Alert
                type="success"
                showIcon
                closable
                message={submitSuccess}
                onClose={() => setSubmitSuccess(null)}
                style={{ marginBottom: 16 }}
              />
            )}
            <Form.Item name="unitSize" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="kegMl" hidden>
              <Input />
            </Form.Item>

            <Form.Item label="Format (from SKU)">
              <Text>
                {dtcPack.packType === 'Keg'
                  ? `${kegLitresLabelFromUnitMl(dtcPack.unitMl) ?? dtcPack.unitSize}L Keg`
                  : (
                    <>
                      {dtcPack.packType}
                      {' '}
                      —
                      {' '}
                      {dtcPack.unitSize}
                      ml
                    </>
                  )}
              </Text>
            </Form.Item>

            <Form.Item
              name="selectedRRP"
              label={rrpOptions.length > 0 ? 'Select RRP' : 'Enter RRP'}
              rules={[{ required: true, message: 'Please enter or select an RRP value' }]}
              extra={rrpOptions.length === 0 ? (
                <Text type="secondary">
                  No RRP on file — enter keg retail price, or set RRP Unit on the SKU in Product Editor.
                </Text>
              ) : undefined}
            >
              {rrpOptions.length > 0 ? (
                <Select placeholder="RRP">
                  {rrpOptions.map((value) => (
                    <Option key={value} value={value}>{value}</Option>
                  ))}
                </Select>
              ) : (
                <InputNumber
                  placeholder="RRP"
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                />
              )}
            </Form.Item>

            <Form.Item
              name="SKU"
              label="Shopify SKU prefix"
              rules={[{ required: true, message: 'SKU is required' }]}
            >
              <Input placeholder="Up to 5 letters" maxLength={5} />
            </Form.Item>

            {dtcPack.unitSize === '440' && (
              <Form.Item
                name="selectedSlab"
                label="Select slab size"
                rules={[{ required: true, message: 'Please select a slab size.' }]}
              >
                <Radio.Group>
                  <Radio value={15}>15-Pack</Radio>
                  <Radio value={16}>16-Pack</Radio>
                  <Radio value={24}>24-Pack</Radio>
                </Radio.Group>
              </Form.Item>
            )}

            {variantPricing && (
              <>
                {variantPricing.warnings.map((msg) => (
                  <Alert
                    key={msg}
                    type="warning"
                    showIcon
                    message={msg}
                    style={{ marginBottom: 16 }}
                  />
                ))}
                <Form.Item label="Variant prices">
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="title"
                    dataSource={variantPricing.variants}
                    onRow={(row) => (
                      row.source === 'sku'
                        ? { style: { background: '#f6ffed' } }
                        : {}
                    )}
                    columns={[
                      {
                        title: 'Variant',
                        dataIndex: 'title',
                        key: 'title',
                        width: 120,
                        onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                      },
                      {
                        title: 'Price',
                        dataIndex: 'price',
                        key: 'price',
                        render: (price, row) => (
                          row.source === 'sku'
                            ? (
                              <Text strong style={{ color: '#389e0d' }}>
                                {`$${price}`}
                              </Text>
                            )
                            : `$${price}`
                        ),
                      },
                      {
                        title: 'Source',
                        key: 'source',
                        render: (_, row) => (
                          row.source === 'sku' ? (
                            <>
                              <Tag color="green">SKU</Tag>
                              {sourceLabel(row, watchedRrp)}
                            </>
                          ) : sourceLabel(row, watchedRrp)
                        ),
                      },
                    ]}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item
              name="productFilters"
              label="Preconfigure available Shopify filters"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Select
                mode="multiple"
                placeholder="Select filters"
                style={{ width: '100%' }}
                options={filterOptions.map((option) => ({ value: option, label: option }))}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<ToolOutlined />}
                loading={submitting}
                block
                size="large"
              >
                Create draft product in Shopify
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      <Card style={{ marginTop: 16 }} size="small">
        <Text type="secondary">
          Prefer the table-based flow or legacy barcode columns?
          {' '}
          <Link to="/dashboard/dtc-product-builder/shopify">Open legacy builder</Link>
        </Text>
      </Card>
    </div>
  );
};

NewDTCProductBuilder.propTypes = {
  prices: PropTypes.arrayOf(PropTypes.object),
  rules: PropTypes.objectOf(PropTypes.string),
};

NewDTCProductBuilder.defaultProps = {
  prices: [],
  rules: undefined,
};

export default NewDTCProductBuilder;
